// SavedSessions.jsx
import React, { useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useListGradingSessionsQuery,
  useDeleteGradingSessionMutation,
  useDownloadGradingSessionMutation
} from "../../slices/authApi";
import classes from "../CriteriaSubmission/examples//examples.module.css";
import ArrowLeft from "../../images/left-arrow.svg";
import ArrowLeftEmpty from "../../images/left-arrow-empty.svg";
import ArrowRight from "../../images/right-arrow.svg";
import ArrowRightEmpty from "../../images/right-arrow-empty.svg";
import styles from "./SavedSessions.module.css";
import Modal from "../../common/Modal";
import util from "../../styles/utilities.module.css";
import {
  setSessionName,
  setSessionDescription,
} from "../../slices/gradingForm";

const SavedSessions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: savedSessions,
    isLoading,
    isError,
  } = useListGradingSessionsQuery();
  const [deleteSession] = useDeleteGradingSessionMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const sessionDeleteRef = useRef(null);
  const [downloadSession] = useDownloadGradingSessionMutation();
  const [isDebugUser, setIsDebugUser] = useState(false);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user")).userData;
        if (user?.user_options?.show_debug_data) {
            setIsDebugUser(true);
        }
    }, []);

  function SampleNextArrow(props) {
    const { className, style, onClick, isGrading } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          top: isGrading ? "20%" : "10%",
        }}
        onClick={onClick}
      >
        <img
          height={20}
          width={20}
          src={
            className.includes("slick-disabled") ? ArrowRightEmpty : ArrowRight
          }
          alt="arrow_left"
        />
      </div>
    );
  }

  function SamplePrevArrow(props) {
    const { className, style, onClick, isGrading } = props;

    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          top: isGrading ? "20%" : "10%",
        }}
        onClick={onClick}
      >
        <img
          height={20}
          width={20}
          src={
            className.includes("slick-disabled") ? ArrowLeftEmpty : ArrowLeft
          }
          alt="arrow_left"
        />
      </div>
    );
  }

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow isGrading={true} />,
    prevArrow: <SamplePrevArrow isGrading={true} />,
    dotsClass: classes.buttonBar,
  };

  const handleSessionSelect = (session) => {
    // Save name/description for potential display and re-saving later
    dispatch(setSessionName(session.name));
    dispatch(setSessionDescription(session.description));
    navigate(`/load-session/${session.id}`);
  };

  const handleConfirmDeleteSession = (session) => {
    setConfirmDelete(true);
    sessionDeleteRef.current = session;
  };

  const handleDownloadSession = async (session) => {
    try {
      const downloadedSession = await downloadSession(session.id).unwrap();
  
      // Create a blob with the downloaded data
      const blob = new Blob([JSON.stringify(downloadedSession, null, 2)], { type: "application/json" });
  
      // Create a temporary URL for the blob
      const url = URL.createObjectURL(blob);
  
      // Create a link element
      const a = document.createElement("a");
      a.href = url;
      a.download = `session_${session.id}.json`; // Set the download file name
  
      // Append the link to the document and trigger the download
      document.body.appendChild(a);
      a.click();
  
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download session:", err);
    }
  };
  

  const handleDeleteSession = async () => {
    try {
      await deleteSession(sessionDeleteRef.current.id).unwrap();
      // Invalidated tag should automatically refetch/redisplay the data
    } catch (err) {
      console.error("Failed to delete session:", err);
    }finally { setConfirmDelete(false)}
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading sessions</div>;

  return savedSessions.sessions.length > 0 ? (
    <>
      {confirmDelete ? (
        <Modal>
          <div className={classes.infoModalContainer}>
            <p className={styles.sessionHeading}> <b>{`Delete Session ${sessionDeleteRef.current.name} ?`}</b></p>
            <div className={ `${styles.buttonContainer} ${styles.delbuttonContainer} ` }>
              <button className={`${util.btn} ${util['btn--dangerSubtle']} ${util['btn-sm']} ${styles.delbutton}`}  onClick={handleDeleteSession}>Delete</button>
              <button
                className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']} ${styles.buttonAlt}`}
                type="button"
                onClick={() => { setConfirmDelete(false); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
      <div className={styles.sliderContainer}>
        <h2>Saved Sessions</h2>
        <Slider {...settings}>
          {savedSessions.sessions.map((session) => (
            <div className={styles.singleSlide} key={session.id}>
              <p className={styles.sessionHeading}>
                <b>{session.name}</b>
              </p>
              <p className={styles.sessionDescription}>{session.description}</p>

              <div className={styles.buttonContainer}>
                <button
                  className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']} ${styles.delbutton}`}
                  onClick={() => handleConfirmDeleteSession(session)}
                  type="button"
                >
                  Delete
                </button>
                { isDebugUser && (
                                  <button
                                    className={`${util.btn} ${util['btn--dangerSubtle']} ${util['btn-sm']} ${styles.delbutton}`}
                                    onClick={() => handleDownloadSession(session)}
                                    type="button"
                                  >
                                    Download test data
                                  </button>
                  )}
                <button
                  className={`${util.btn} ${util['btn--primary']} ${util['btn-sm']} ${styles.button}`}
                  onClick={() => handleSessionSelect(session)}
                  type="button"
                >
                  Load
                </button>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </>
  ) : null;
};

export default SavedSessions;
